import type {
  Action,
  ForeachAction,
  IfAction,
  Position,
  SwitchAction,
  Trigger,
  UntilAction,
  WorkflowDefinition,
} from '@/types/workflowDefinition';
import {
  isForeachAction,
  isIfAction,
  isSwitchAction,
  isUntilAction,
} from '@/types/workflowDefinition';

/**
 * Bridge between the canonical layout store (keys: React Flow node IDs) and
 * the layout helpers (which still read/write `action.metadata.position` as a
 * transient computation slot).
 *
 * Workflows in memory keep `metadata.position` as an OPTIONAL field, but the
 * serializer strips it on save and `setWorkflowFiles` strips it on load.
 * Inside the auto-layout pipeline we hydrate it from the layout store and
 * extract the result back out — the helpers themselves never have to learn
 * about the store. This keeps the helpers pure and unit-testable.
 *
 * Key convention (matches `useWorkflowGraph` and `applyLayoutPositions`):
 *   - triggers:        `trigger-{name}`
 *   - top-level action: `action-{name}`
 *   - nested action:    `action-{scopedPath}` (slash-joined, e.g. `action-Until/HTTP_5`)
 */

const ACTION_PREFIX = 'action-';
const TRIGGER_PREFIX = 'trigger-';

function makeActionKey(scopePath: string, name: string): string {
  return scopePath ? `${ACTION_PREFIX}${scopePath}/${name}` : `${ACTION_PREFIX}${name}`;
}

/**
 * Inject positions from the global map into a single actions record (and all
 * nested scopes within it). Returns a new tree; the input is left untouched.
 */
export function injectActionPositions(
  actions: Record<string, Action>,
  positions: Record<string, Position>,
  scopePath = '',
): Record<string, Action> {
  const out: Record<string, Action> = {};
  for (const [name, action] of Object.entries(actions)) {
    out[name] = injectIntoAction(action, positions, scopePath, name);
  }
  return out;
}

function injectIntoAction(
  action: Action,
  positions: Record<string, Position>,
  scopePath: string,
  name: string,
): Action {
  const key = makeActionKey(scopePath, name);
  const childPath = scopePath ? `${scopePath}/${name}` : name;
  const externalPosition = positions[key];
  // Preserve any existing `metadata.position` (e.g., one a caller just set
  // for an in-flight insertion) unless the store has its own — store wins.
  const next: Action = externalPosition
    ? { ...action, metadata: { ...(action.metadata ?? {}), position: externalPosition } }
    : { ...action };

  if (isIfAction(next)) {
    const ifAction = next as IfAction;
    if (ifAction.actions) {
      (next as IfAction).actions = injectActionPositions(ifAction.actions, positions, childPath);
    }
    if (ifAction.else?.actions) {
      (next as IfAction).else = {
        ...ifAction.else,
        actions: injectActionPositions(ifAction.else.actions, positions, childPath),
      };
    }
  } else if (isForeachAction(next) || isUntilAction(next)) {
    const loopAction = next as ForeachAction | UntilAction;
    if (loopAction.actions) {
      (next as ForeachAction | UntilAction).actions = injectActionPositions(loopAction.actions, positions, childPath);
    }
  } else if (isSwitchAction(next)) {
    const switchAction = next as SwitchAction;
    if (switchAction.cases) {
      const nextCases: SwitchAction['cases'] = {};
      for (const [caseName, caseObj] of Object.entries(switchAction.cases)) {
        const tc = caseObj as { case: string; actions?: Record<string, Action> };
        nextCases[caseName] = tc.actions
          ? { ...tc, actions: injectActionPositions(tc.actions, positions, childPath) }
          : tc;
      }
      (next as SwitchAction).cases = nextCases;
    }
    if (switchAction.default?.actions) {
      (next as SwitchAction).default = {
        ...switchAction.default,
        actions: injectActionPositions(switchAction.default.actions, positions, childPath),
      };
    }
  }

  return next;
}

/**
 * Inject trigger positions from the global map into a triggers record.
 */
export function injectTriggerPositions(
  triggers: Record<string, Trigger>,
  positions: Record<string, Position>,
): Record<string, Trigger> {
  const out: Record<string, Trigger> = {};
  for (const [name, trigger] of Object.entries(triggers)) {
    const key = `${TRIGGER_PREFIX}${name}`;
    const external = positions[key];
    out[name] = external
      ? { ...trigger, metadata: { ...(trigger.metadata ?? {}), position: external } }
      : trigger;
  }
  return out;
}

/**
 * Convenience wrapper: produces a workflow with all positions hydrated from
 * the layout map. Caller is responsible for not persisting the result.
 */
export function injectWorkflowPositions(
  workflow: WorkflowDefinition,
  positions: Record<string, Position>,
): WorkflowDefinition {
  return {
    ...workflow,
    definition: {
      ...workflow.definition,
      triggers: injectTriggerPositions(workflow.definition.triggers ?? {}, positions),
      actions: injectActionPositions(workflow.definition.actions ?? {}, positions),
    },
  };
}

/**
 * Walk a (possibly position-injected) actions tree and return the position
 * map keyed by React Flow node ID. Actions without a `metadata.position`
 * contribute nothing.
 */
export function extractActionPositions(
  actions: Record<string, Action>,
  scopePath = '',
  out: Record<string, Position> = {},
): Record<string, Position> {
  for (const [name, action] of Object.entries(actions)) {
    const pos = action.metadata?.position as Position | undefined;
    if (pos && Number.isFinite(pos.x) && Number.isFinite(pos.y)) {
      out[makeActionKey(scopePath, name)] = { x: pos.x, y: pos.y };
    }
    const childPath = scopePath ? `${scopePath}/${name}` : name;
    if (isIfAction(action)) {
      const ifAction = action as IfAction;
      if (ifAction.actions) extractActionPositions(ifAction.actions, childPath, out);
      if (ifAction.else?.actions) extractActionPositions(ifAction.else.actions, childPath, out);
    } else if (isForeachAction(action) || isUntilAction(action)) {
      const loopAction = action as ForeachAction | UntilAction;
      if (loopAction.actions) extractActionPositions(loopAction.actions, childPath, out);
    } else if (isSwitchAction(action)) {
      const switchAction = action as SwitchAction;
      if (switchAction.cases) {
        for (const [, caseObj] of Object.entries(switchAction.cases)) {
          const tc = caseObj as { case: string; actions?: Record<string, Action> };
          if (tc.actions) extractActionPositions(tc.actions, childPath, out);
        }
      }
      if (switchAction.default?.actions) extractActionPositions(switchAction.default.actions, childPath, out);
    }
  }
  return out;
}

export function extractTriggerPositions(
  triggers: Record<string, Trigger>,
  out: Record<string, Position> = {},
): Record<string, Position> {
  for (const [name, trigger] of Object.entries(triggers)) {
    const pos = trigger.metadata?.position as Position | undefined;
    if (pos && Number.isFinite(pos.x) && Number.isFinite(pos.y)) {
      out[`${TRIGGER_PREFIX}${name}`] = { x: pos.x, y: pos.y };
    }
  }
  return out;
}

/**
 * Walk an actions tree and return a new tree with `metadata.position`
 * removed everywhere. Used by `setWorkflowFiles` after positions have been
 * lifted into the layout store and by `addAction` after the layout helpers
 * have run.
 */
export function stripActionPositions(
  actions: Record<string, Action>,
): Record<string, Action> {
  const out: Record<string, Action> = {};
  for (const [name, action] of Object.entries(actions)) {
    out[name] = stripPositionsFromAction(action);
  }
  return out;
}

function stripPositionsFromAction(action: Action): Action {
  let next: Action = action;
  if (action.metadata && ('position' in action.metadata || 'toolNodePositions' in action.metadata)) {
    const { position: _p, toolNodePositions: _t, ...restMetadata } = action.metadata as Record<string, unknown>;
    void _p; void _t;
    next = Object.keys(restMetadata).length > 0
      ? { ...action, metadata: restMetadata }
      : (() => {
        const { metadata: _m, ...rest } = action as Record<string, unknown>;
        void _m;
        return rest as Action;
      })();
  }

  if (isIfAction(next)) {
    const ifAction = next as IfAction;
    let changed = false;
    let newActions = ifAction.actions;
    let newElse = ifAction.else;
    if (ifAction.actions) {
      newActions = stripActionPositions(ifAction.actions);
      if (newActions !== ifAction.actions) changed = true;
    }
    if (ifAction.else?.actions) {
      const strippedElseActions = stripActionPositions(ifAction.else.actions);
      if (strippedElseActions !== ifAction.else.actions) {
        newElse = { ...ifAction.else, actions: strippedElseActions };
        changed = true;
      }
    }
    if (changed) next = { ...ifAction, actions: newActions, else: newElse } as Action;
  } else if (isForeachAction(next) || isUntilAction(next)) {
    const loopAction = next as ForeachAction | UntilAction;
    if (loopAction.actions) {
      const stripped = stripActionPositions(loopAction.actions);
      if (stripped !== loopAction.actions) {
        next = { ...loopAction, actions: stripped } as Action;
      }
    }
  } else if (isSwitchAction(next)) {
    const switchAction = next as SwitchAction;
    let changed = false;
    let nextCases = switchAction.cases;
    let nextDefault = switchAction.default;
    if (switchAction.cases) {
      const built: NonNullable<SwitchAction['cases']> = {};
      let casesChanged = false;
      for (const [caseName, caseObj] of Object.entries(switchAction.cases)) {
        const tc = caseObj as { case: string; actions?: Record<string, Action> };
        if (tc.actions) {
          const stripped = stripActionPositions(tc.actions);
          if (stripped !== tc.actions) casesChanged = true;
          built[caseName] = { ...tc, actions: stripped };
        } else {
          built[caseName] = tc;
        }
      }
      if (casesChanged) { nextCases = built; changed = true; }
    }
    if (switchAction.default?.actions) {
      const stripped = stripActionPositions(switchAction.default.actions);
      if (stripped !== switchAction.default.actions) {
        nextDefault = { ...switchAction.default, actions: stripped };
        changed = true;
      }
    }
    if (changed) next = { ...switchAction, cases: nextCases, default: nextDefault } as Action;
  }

  return next;
}

export function stripTriggerPositions(
  triggers: Record<string, Trigger>,
): Record<string, Trigger> {
  const out: Record<string, Trigger> = {};
  for (const [name, trigger] of Object.entries(triggers)) {
    if (trigger.metadata && ('position' in trigger.metadata)) {
      const { position: _p, ...restMetadata } = trigger.metadata as Record<string, unknown>;
      void _p;
      out[name] = Object.keys(restMetadata).length > 0
        ? { ...trigger, metadata: restMetadata }
        : (() => {
          const { metadata: _m, ...rest } = trigger as Record<string, unknown>;
          void _m;
          return rest as Trigger;
        })();
    } else {
      out[name] = trigger;
    }
  }
  return out;
}

/**
 * Walk an agent action tree and return the tool node positions keyed by
 * agent action name. Used by the legacy migration on load.
 */
export function extractToolNodePositions(
  actions: Record<string, Action>,
  out: Record<string, Record<string, Position>> = {},
): Record<string, Record<string, Position>> {
  for (const [name, action] of Object.entries(actions)) {
    const tnp = action.metadata?.toolNodePositions as Record<string, Position> | undefined;
    if (tnp && Object.keys(tnp).length > 0) {
      out[name] = { ...tnp };
    }
    if (isIfAction(action)) {
      const ifAction = action as IfAction;
      if (ifAction.actions) extractToolNodePositions(ifAction.actions, out);
      if (ifAction.else?.actions) extractToolNodePositions(ifAction.else.actions, out);
    } else if (isForeachAction(action) || isUntilAction(action)) {
      const loopAction = action as ForeachAction | UntilAction;
      if (loopAction.actions) extractToolNodePositions(loopAction.actions, out);
    } else if (isSwitchAction(action)) {
      const switchAction = action as SwitchAction;
      if (switchAction.cases) {
        for (const [, caseObj] of Object.entries(switchAction.cases)) {
          const tc = caseObj as { case: string; actions?: Record<string, Action> };
          if (tc.actions) extractToolNodePositions(tc.actions, out);
        }
      }
      if (switchAction.default?.actions) extractToolNodePositions(switchAction.default.actions, out);
    }
  }
  return out;
}
